import { ProjectCreateError } from '@/libraries/bxp-web-stargate-client/src/util/unsuccessful-fetch-error';

const MAX_API_ERROR_MESSAGE_LENGTH = 500;

export type JsmUseCaseProjectFailure = {
	readonly apiErrorMessage?: string;
	readonly backendTraceId?: string;
	readonly errorMessage: string;
	readonly httpStatus?: number;
};

type GetJsmUseCaseProjectFailureOptions = {
	readonly error: unknown;
	readonly projectKey: string;
	readonly projectName: string;
};

export const getJsmUseCaseProjectFailure = async ({
	error,
	projectKey,
	projectName,
}: GetJsmUseCaseProjectFailureOptions): Promise<JsmUseCaseProjectFailure> => {
	const errorMessage = error instanceof Error ? error.message : 'Unknown error';

	if (!(error instanceof ProjectCreateError)) {
		return { errorMessage };
	}

	const response = error.response;
	const apiErrorMessage = await getSanitizedApiErrorMessage(response, [projectName, projectKey]);

	return {
		apiErrorMessage,
		backendTraceId: response.headers.get('atl-traceid') ?? undefined,
		errorMessage,
		httpStatus: response.status,
	};
};

const getSanitizedApiErrorMessage = async (
	response: Response,
	redactedValues: readonly string[],
): Promise<string | undefined> => {
	if (!response.headers.get('content-type')?.toLowerCase().includes('application/json')) {
		return undefined;
	}

	try {
		const responseBody: unknown = await response.clone().json();
		if (!isRecord(responseBody)) {
			return undefined;
		}

		const messages = [
			...(Array.isArray(responseBody['errorMessages'])
				? responseBody['errorMessages'].filter(
						(message): message is string => typeof message === 'string',
					)
				: []),
			...(isRecord(responseBody['errors'])
				? Object.values(responseBody['errors']).filter(
						(message): message is string => typeof message === 'string',
					)
				: []),
		]
			.map((message) => sanitizeMessage(message, redactedValues))
			.filter((message) => message.length > 0);

		const combinedMessage = [...new Set(messages)].join('; ');
		if (!combinedMessage) {
			return undefined;
		}

		return combinedMessage.length <= MAX_API_ERROR_MESSAGE_LENGTH
			? combinedMessage
			: `${combinedMessage.slice(0, MAX_API_ERROR_MESSAGE_LENGTH - 1)}…`;
	} catch {
		return undefined;
	}
};

const sanitizeMessage = (message: string, redactedValues: readonly string[]): string => {
	const redactedMessage = redactedValues
		.filter((value) => value.length > 0)
		.reduce(
			(value, redactedValue) =>
				value
					.split(redactedValue)
					.join(redactedValue === redactedValues[0] ? '<project-name>' : '<project-key>'),
			message,
		);

	return redactedMessage
		.replace(/\p{Cc}+/gu, ' ')
		.replace(/\s+/gu, ' ')
		.trim();
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);
